import { Store, ContextStore, PageStore } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { CarDropOffSearch, ContextInput } from 'src/common/__generated__/api';
import { computed, observable } from 'mobx';
import {
  formatTimeForTwelveHourFormat,
  IFormatedDateTime,
  parseTimeFromURL,
  resolveDate,
  resolveTime
} from 'helpers/date-time-resolver';

// TODO Move all this to search-criteria-store once drop off page is done

// Drop off graph query request params
export interface IDropOffQuery {
  readonly pickUpLoc?: string;
  readonly pickupRegion?: string;
  readonly pickUpLat?: number;
  readonly pickUpLong?: number;
  readonly dropLoc?: string;
  readonly dropRegion?: string;
  readonly dropLat?: number;
  readonly dropLong?: number;
  readonly pickUpDate?: string;
  readonly dropDate?: string;
  readonly pickUpTime?: string;
  readonly dropTime?: string;
  readonly radius?: number;
  readonly view?: string;
  readonly paymentMode?: string;
  readonly prepaid?: boolean;
  readonly offerQualifiers?: string;
  readonly pickUpRentalLocationId?: string;
  readonly carClassFilter?: string;
  readonly showOnlyCarsWithAC?: boolean;
  readonly carTransmissionType?: string;
  readonly carMileageType?: string;
  readonly showOnlyCarsWithUnlimitedMileage?: boolean;
  readonly priceShown?: number;
  readonly dailyPriceShown?: boolean;
  readonly withinCP?: boolean;
  readonly selVen?: string;
  readonly avgCatPR?: string;
  readonly originalOfferPIID?: string;
  readonly rfrr?: string;
  readonly reservationGuaranteeRequired?: boolean;
}

// Search query parameters
export interface ISearchQueryInDropOffParam {
  readonly locn?: string;
  readonly olat?: string;
  readonly olon?: string;
  readonly dpln?: string;
  readonly loc2?: string;
  readonly dlat?: string;
  readonly dlon?: string;
  readonly drid1?: string;
  readonly date1?: string;
  readonly date2?: string;
  readonly time1?: string;
  readonly time2?: string;
  readonly rfrr?: string;
}

export const searchParams = ['locn', 'olat', 'olon', 'dpln', 'loc2', 'dlat', 'dlon', 'drid1', 'date1', 'date2', 'time1', 'time2', 'rfrr'];

export const searchQueryParams = [
  'pickUpLoc',
  'pickupRegion',
  'pickUpLat',
  'pickUpLong',
  'dropLoc',
  'dropRegion',
  'dropLat',
  'dropLong',
  'pickUpDate',
  'dropDate',
  'pickUpTime',
  'dropTime'
];

// Mapping between drop off URL query parameters with graph query request params
export enum dropOffSearchParamsMap {
  locn = 'pickUpLoc',
  olat = 'pickUpLat',
  olon = 'pickUpLong',
  loc2 = 'dropLoc',
  dlat = 'dropLat',
  dlon = 'dropLong',
  date1 = 'pickUpDate',
  date2 = 'dropDate',
  time1 = 'pickUpTime',
  time2 = 'dropTime',
  dpln = 'pickupRegion',
  drid1 = 'dropRegion',
  rdus = 'radius',
  vend = 'selVen',
  view = 'view',
  paymentMode = 'paymentMode',
  prepaid = 'prepaid',
  offerQualifiers = 'offerQualifiers',
  pickUpRentalLocationId = 'pickUpRentalLocationId',
  carClassFilter = 'carClassFilter',
  showOnlyCarsWithAC = 'showOnlyCarsWithAC',
  carTransmissionType = 'carTransmissionType',
  carMileageType = 'carMileageType',
  showOnlyCarsWithUnlimitedMileage = 'showOnlyCarsWithUnlimitedMileage',
  priceShown = 'priceShown',
  dailyPriceShown = 'dailyPriceShown',
  withinCP = 'withinCP',
  avgCatPR = 'avgCatPR',
  originalOfferPIID = 'originalOfferPIID',
  rfrr = 'rfrr',
  reservationGuaranteeRequired = 'reservationGuaranteeRequired',
  useRewards = 'useRewards'
}

export class DropOffCriteriaStore extends Store {
  private context: ContextInput;

  private contextStore: ContextStore;

  private page: PageStore;

  private formattedDateTime: IFormatedDateTime = {} as IFormatedDateTime;

  private dateTimeKeys = {
    pickUpDate : 'pickUpDate',
    dropDate   : 'dropDate',
    pickUpTime : 'pickUpTime',
    dropTime   : 'dropTime'
  };

  @observable
  public dropOffQuery: IDropOffQuery = {};

  @observable
  public searchQueryInDropOffParam: ISearchQueryInDropOffParam;

  public setContext(context: ContextStore): void {
    this.context = getBexApiContext(context);
    this.contextStore = context;
  }

  public setPage(page: PageStore): void {
    this.page = page;
  }

  public setDropOffQuery(query: IDropOffQuery): void {
    const searchUrlParams = {};
    Object.keys(query).map(key => {
      if (key !== 'prevQuery') {
        const paramKey = dropOffSearchParamsMap[key] ? dropOffSearchParamsMap[key] : '';
        const searchFilter = key.match(/sel.*/);
        if (paramKey || searchFilter) {
          const decodedParam = decodeURIComponent(query[key]);
          this.dropOffQuery[paramKey] = decodedParam;

          if (paramKey === this.dateTimeKeys[paramKey]) {
            this.setupDateParams(paramKey, this.dropOffQuery[paramKey]);
          }

          if (searchParams.includes(key)) {
            searchUrlParams[key] = query[key];
          }

          if (searchFilter) {
            const values = query[key].slice(1, -1);
            const valuesArray = values.split(',');
            if (Array.isArray(valuesArray)) {
              const formattedValues = '"' + valuesArray.join('","') + '"';
              searchUrlParams[key] = '[' + formattedValues + ']';
            } else {
              searchUrlParams[key] = '"' + query[key] + '"';
            }
          }
        }
      }
    });

    this.searchQueryInDropOffParam = { ...searchUrlParams };
  }

  public hydrate(data: SerializedData): void {
    this.context = { ...data.context };
    this.formattedDateTime = { ...data.formattedDateTime };
    this.dropOffQuery = { ...data.dropOffQuery };
    this.searchQueryInDropOffParam = { ...data.searchQueryInDropOffParam };
  }

  @computed
  public get dropOffVariables(): CarDropOffSearch.Variables {
    const {
      formattedPickupDate: pickupDate,
      formattedPickupTime: pickUpTime,
      formattedDropDate: dropDate,
      formattedDropTime: dropTime
    } = this.formattedDateTime;

    const { dropRegion, pickupRegion, pickUpLoc, pickUpLat, pickUpLong, dropLoc, dropLat, dropLong } = this.dropOffQuery;
    
    const coordinatesPickUp = (pickUpLat && pickUpLong) ? {
      latitude  : Number(pickUpLat),
      longitude : Number(pickUpLong)
    } : null;

    const coordinatesDropOff = (dropLat && dropLong) ? {
      latitude  : Number(dropLat),
      longitude : Number(dropLong)
    } : null;

    return {
      context                  : this.context,
      primaryCarSearchCriteria : {
        pickUpLocation : {
          searchTerm  : pickUpLoc || undefined,
          regionId    : pickupRegion || undefined,
          coordinates : coordinatesPickUp
        },
        dropOffLocation : {
          searchTerm  : dropLoc || pickUpLoc,
          regionId    : dropLoc ? dropRegion || undefined : pickupRegion || undefined,
          coordinates : coordinatesDropOff
        },
        pickUpDateTime : {
          day    : pickupDate && pickupDate.day ? pickupDate.day : undefined,
          month  : pickupDate && pickupDate.month ? pickupDate.month : undefined,
          year   : pickupDate && pickupDate.year ? pickupDate.year : undefined,
          hour   : pickUpTime && pickUpTime.hour !== undefined ? pickUpTime.hour : 10,
          minute : pickUpTime && pickUpTime.minute !== undefined ? pickUpTime.minute : 30,
          second : 0
        },
        dropOffDateTime : {
          day    : dropDate && dropDate.day ? dropDate.day : undefined,
          month  : dropDate && dropDate.month ? dropDate.month : undefined,
          year   : dropDate && dropDate.year ? dropDate.year : undefined,
          hour   : dropTime && dropTime.hour !== undefined ? dropTime.hour : 10,
          minute : dropTime && dropTime.minute !== undefined ? dropTime.minute : 30,
          second : 0
        }
      },
      secondaryCriteria : {
        selections : this.getSelections()
      }
    };
  }

  private setupDateParams(key: string, stringFormattedDate: string) {
    const { localDateFormat } = this.contextStore;
    switch (key) {
    case 'pickUpDate':
      this.formattedDateTime.formattedPickupDate = resolveDate(stringFormattedDate, localDateFormat);
      break;
    case 'dropDate':
      this.formattedDateTime.formattedDropDate = resolveDate(stringFormattedDate, localDateFormat);
      break;
    case 'pickUpTime':
      this.formattedDateTime.formattedPickupTime = resolveTime(
        formatTimeForTwelveHourFormat(parseTimeFromURL(stringFormattedDate))
      );
      break;
    case 'dropTime':
      this.formattedDateTime.formattedDropTime = resolveTime(
        formatTimeForTwelveHourFormat(parseTimeFromURL(stringFormattedDate))
      );
      break;
    }
  }

  // Function to populate drop off specific query parameters (excluding search)
  private getSelections = () => {
    const dropOffSelections = [];
    Object.keys(this.dropOffQuery).map(key => {
      if (!searchQueryParams.includes(key)) {
        if (key === 'useRewards') {
          dropOffSelections.push({ id : key, value : this.dropOffQuery[key] ? 'ApplyRewardsPoints' : 'DoNotApplyRewardsPoints' });
        } else {
          dropOffSelections.push({
            id    : key,
            value : this.dropOffQuery[key]
          });
        }
      }
    });

    return dropOffSelections;
  };
}
